import { observer } from 'mobx-react-lite';
import { useStore } from '@shared/hooks/useStore';
import { Link, useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES, ROUTES } from '@/shared/configs/constants';
import Logo from '@shared/assets/images/logos/logo.svg';
import IconImages from '@shared/assets/images/icons';
import { ChevronDown, LogOut, Menu, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const AdminHeader = observer(() => {
    const { authStore: { profile, logout } } = useStore();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);

    const navLinks = [
        { label: 'ユーザー定期購読一覧', href: ADMIN_ROUTES.userSubscriptions.href },
        { label: 'ユーザー管理', href: ADMIN_ROUTES.userManagement.href },
        { label: 'プラン管理', href: ADMIN_ROUTES.planManagement.href },
        { label: '利用ガイド', href: ROUTES.usageGuide.href }
    ];

    const handleLogout = () => {
        logout();
    };

    const closeMenu = () => setIsMenuOpen(false);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setIsUserMenuOpen(false);
            }
        };

        if (isUserMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isUserMenuOpen]);

    // Mobile Menu Component
    const MobileMenu = () => {
        if (!isMenuOpen) return null;

        return (
            <div className='fixed inset-0 z-50 bg-white lg:hidden flex flex-col'>
                <div className='flex items-center justify-between px-4 h-[64px] border-b'>
                    <button onClick={closeMenu} className='p-1'>
                        <X className='w-6 h-6' />
                    </button>
                    <img
                        src={Logo}
                        alt='じほう電子コンテンツストア'
                        className='h-10 cursor-pointer'
                        onClick={() => {
                            navigate(ADMIN_ROUTES.userManagement.href);
                            closeMenu();
                        }}
                    />
                    <div className='w-8' />
                </div>

                <div className='overflow-y-auto flex-1 px-5 py-4'>
                    <nav className='flex flex-col'>
                        {navLinks.map(link => (
                            <Link
                                key={link.label}
                                to={link.href}
                                className='py-4 text-sm font-bold border-b border-gray-100 hover:text-primary transition-colors'
                                onClick={closeMenu}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>
                </div>
            </div>
        );
    };

    return (
        <header className='sticky top-0 z-30 bg-white border-b'>
            <div className='container mx-auto px-4 lg:px-6 h-[64px] flex items-center justify-between relative'>
                {/* Logo & Desktop Nav */}
                <div className='flex items-center gap-4 lg:gap-8'>
                    {/* Hamburger button for mobile */}
                    <button
                        onClick={() => setIsMenuOpen(true)}
                        className='lg:hidden p-1 -ml-1'
                        aria-label='メニュー'
                    >
                        <Menu className='w-6 h-6' />
                    </button>

                    <Link to='/' className='hidden lg:block shrink-0'>
                        <img src={Logo} alt='じほう電子コンテンツストア' className='h-12' />
                    </Link>

                    {/* Navigation - Hidden on mobile */}
                    <nav className='hidden lg:flex items-center gap-8 xl:gap-12'>
                        {navLinks.map(link => (
                            <Link
                                key={link.label}
                                to={link.href}
                                className='leading-[26px] font-bold text-sm hover:text-primary transition-colors whitespace-nowrap'
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>
                </div>

                <Link to='/' className='lg:hidden absolute left-1/2 -translate-x-1/2'>
                    <img src={Logo} alt='じほう電子コンテンツストア' className='h-10' />
                </Link>

                {/* User Profile */}
                <div className='relative' ref={userMenuRef}>
                    <button
                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                        className='flex items-center gap-2 lg:gap-3 hover:opacity-80 transition-opacity outline-none'
                    >
                        <div className='w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center overflow-hidden border border-gray-100'>
                            <img src={IconImages.avatarIc} alt='' className='w-full h-full object-cover' />
                        </div>
                        <span className='text-sm font-bold hidden md:block'>
                            {profile?.fullName && profile.fullName.length > 20 ? `${profile.fullName.slice(0, 20)}...` : profile?.fullName}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu - Desktop */}
                    {isUserMenuOpen && (
                        <div className='absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 animate-in fade-in zoom-in duration-200'>
                            <Link
                                to={ADMIN_ROUTES.profile.href}
                                className='block px-4 py-2 text-sm hover:bg-gray-50'
                                onClick={() => setIsUserMenuOpen(false)}
                            >
                                マイページ
                            </Link>
                            <hr className='my-2' />
                            <button
                                onClick={handleLogout}
                                className='w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors'
                            >
                                <LogOut className='w-4 h-4' />
                                ログアウト
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <MobileMenu />
        </header>
    );
});

export default AdminHeader;
